import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../components/breadCrumb";
import CheckBox from "../components/common/CheckBox";
import ComboBoxData from "../components/common/ComboBoxData";
import ComboBoxDataMulti from "../components/common/ComboBoxDataMulti";
import { NumericBox } from "../components/common/NumericBox";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { apiGetAllBasesByProduct } from "../services/BasesService";
import { apiGetAllColorsForSampleCard } from "../services/ColorsService";
import { apiGetAllProducts } from "../services/ProductsService";
import { apiGetAllSampleCards, apiGetSampleCardsForProduct } from "../services/SampleCardsService";
import { MultiSelect } from "react-multi-select-component";

import './ExportRecepturPage.css';
import { apiExportReceiptsCsv, apiExportSpecialReceiptsCsv, createCancelSource, isCancel } from "../services/ExportReceipts";
import { useTranslation } from "react-i18next";
import { checkCrud } from "../helpers/userRolesHelper";

interface Props {
    contextLevel: string;

}

type ExportItemState = 'waiting' | 'running' | 'done' | 'error';

interface ExportProductProgress {
    productId: number;
    productName: string;
    sampleCardsTotal: number;
    sampleCardsDone: number;
    currentSampleCardName: string;
    rows: number;
    state: ExportItemState;
    errorMessage: string;
}

interface CsvChunk {
    header: string;
    headerColumns: number;
    rows: string[];
}

// kolik vzorkovnic jednoho vyrobku se stahuje najednou
const PARALLEL_REQUESTS = 4;

const ExportRecepturPage = (props: Props) => {

    let initFormDataState: any =
    {
        'entireSampleCard': false,
        'allSampleCards': false,
        pigmentsRatio: 100,
        sampleCardId: null,
        targetProductIds: [],
        targetProductsAsText: '',
        targetProductsIdsAsText: '',
        pigmentsUnit: 11,
        baseUnit: 11
    };

    const { t } = useTranslation(undefined);


    const [formData, setFormData] = useState(initFormDataState);
    const [sampleCardsForProduct, setSampleCardsForProduct] = useState([]);
    const [allTargetProducts, setAllTargetProducts] = useState([]);
    const [exportProgress, setExportProgress] = useState<ExportProductProgress[] | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState('');
    const cancelSourceRef = useRef<any>(null);
    const canceledRef = useRef(false);
    const productInfo = useSelector((state: any) => state.productInfo);
    const userInfo = useSelector((state: any) => state.userInfo);
    const isReceiptAdmin = checkCrud('receipt', 'admin', userInfo);
    const dispatch = useDispatch();

    useEffect(() => {

        loadData();

    }, []);

    const loadData = async () => {

        let sampleCards: any = await loadSampleCards();
        loadProducts();

        if (sampleCards && sampleCards.length > 0) {
            setFormData({ ...formData, sampleCardId: sampleCards[0].id })
        }
    }


    const loadSampleCards = async () => {

        let sampleCards = await apiGetAllSampleCards();
        setSampleCardsForProduct(sampleCards.data);

        return sampleCards.data;
    }

    const loadProducts = async () => {

        let allProducts = await apiGetAllProducts(false);

        setAllTargetProducts(allProducts.data);
    }

    const handleSampleCardAllCheckedChange = async (checked) => {

        setFormData({ ...formData, allSampleCards: checked });

    }

    const handleSampleCardChange = async (id) => {

        // select vraci hodnotu jako string, dal se porovnava s ciselnym id vzorkovnice
        setFormData({ ...formData, sampleCardId: id !== null && id !== '' ? Number(id) : null });

    }

    const handleTargetProductChange = (selectedOptions) => {

        let displayText = '';
        let idsAsText = '';

        if (selectedOptions && selectedOptions.length > 0) {
            let allSelectedLabels: any[] = [];
            let allSelectedIds: any[] = [];

            selectedOptions.forEach((option: any) => {
                allSelectedLabels.push(option.label);
                allSelectedIds.push(option.value);
            }
            );

            idsAsText = allSelectedIds.join('#');
            displayText = allSelectedLabels.join(', ');
        }

        setFormData({
            ...formData,
            targetProductIds: selectedOptions,
            targetProductsAsText: displayText,
            targetProductsIdsAsText: idsAsText
        });
    }

    const handleUnitChangeBasis = (id) => {
        setFormData({ ...formData, baseUnit: id });
    }

    const handleUnitChangePigments = (id) => {
        setFormData({ ...formData, pigmentsUnit: id });
    }

    const updateProductProgress = (index: number, changes: Partial<ExportProductProgress>) => {
        setExportProgress((previous) => previous
            ? previous.map((item, i) => i === index ? { ...item, ...changes } : item)
            : previous);
    };

    const registerFinishedSampleCard = (index: number, sampleCardName: string, addedRows: number) => {
        setExportProgress((previous) => previous
            ? previous.map((item, i) => i === index
                ? {
                    ...item,
                    sampleCardsDone: item.sampleCardsDone + 1,
                    rows: item.rows + addedRows,
                    currentSampleCardName: sampleCardName
                }
                : item)
            : previous);
    };

    const parseCsvChunk = (text: string): CsvChunk | null => {

        const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.length > 0);

        if (lines.length === 0) {
            return null;
        }

        return {
            header: lines[0],
            headerColumns: (lines[0].match(/;/g) || []).length,
            rows: lines.slice(1)
        };
    };

    // kazda cast exportu je doplnena strednikem az na pocet sloupcu nejsirsi casti, aby sedely sloupce
    const mergeCsvChunks = (chunks: CsvChunk[]): string => {

        const maximumColumns = Math.max(...chunks.map((chunk) => chunk.headerColumns));
        const widestChunk = chunks.find((chunk) => chunk.headerColumns === maximumColumns) as CsvChunk;

        const lines: string[] = [widestChunk.header];

        chunks.forEach((chunk) => {
            const padding = ';'.repeat(2 * (maximumColumns - chunk.headerColumns));
            chunk.rows.forEach((row) => lines.push(row + padding));
        });

        return lines.join('\r\n') + '\r\n';
    };

    const downloadCsv = (content: BlobPart, fileName: string) => {

        const blob = content instanceof Blob ? content : new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const href = URL.createObjectURL(blob);

        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    };

    const handleExportCancel = () => {

        canceledRef.current = true;

        if (cancelSourceRef.current) {
            cancelSourceRef.current.cancel();
        }
    };

    const handleExportCreation = async () => {

        if (isExporting) {
            return;
        }

        if (!formData.targetProductsIdsAsText || formData.targetProductsIdsAsText.length <= 0) {
            alert(t('ExportReceipts.PleaseSelectAtLeastOneProduct'));
            return;
        }

        if (!formData.sampleCardId && !formData.allSampleCards) {
            alert(t('ExportReceipts.PleaseSelectSampleBook'));
            return;
        }

        const selectedProducts = formData.targetProductIds.map((option: any) => ({ id: option.value, name: option.label }));
        const selectedSampleCards: any[] = formData.allSampleCards
            ? sampleCardsForProduct
            : (sampleCardsForProduct as any[]).filter((sampleCard: any) => Number(sampleCard.id) === Number(formData.sampleCardId));

        if (selectedSampleCards.length === 0) {
            alert(t('ExportReceipts.PleaseSelectSampleBook'));
            return;
        }

        canceledRef.current = false;
        cancelSourceRef.current = createCancelSource();

        setIsExporting(true);
        setExportMessage('');
        setExportProgress(selectedProducts.map((product: any) => ({
            productId: product.id,
            productName: product.name,
            sampleCardsTotal: selectedSampleCards.length,
            sampleCardsDone: 0,
            currentSampleCardName: '',
            rows: 0,
            state: 'waiting' as ExportItemState,
            errorMessage: ''
        })));

        // indexovane podle poradi vyrobek/vzorkovnice, aby paralelni stahovani nerozhodilo poradi radku
        const chunksByOrder: (CsvChunk | undefined)[] = new Array(selectedProducts.length * selectedSampleCards.length);
        let hasError = false;

        for (let productIndex = 0; productIndex < selectedProducts.length; productIndex++) {

            if (canceledRef.current) {
                break;
            }

            const product = selectedProducts[productIndex];
            updateProductProgress(productIndex, { state: 'running' });

            let nextSampleCardIndex = 0;

            const exportWorker = async () => {

                while (!canceledRef.current) {

                    const sampleCardIndex = nextSampleCardIndex++;

                    if (sampleCardIndex >= selectedSampleCards.length) {
                        return;
                    }

                    const sampleCard = selectedSampleCards[sampleCardIndex];

                    try {
                        const response = await apiExportReceiptsCsv(
                            `${product.id}`,
                            sampleCard.id,
                            false,
                            formData.pigmentsUnit,
                            formData.baseUnit,
                            cancelSourceRef.current.token);

                        const chunk = parseCsvChunk(await (response.data as Blob).text());

                        if (chunk) {
                            chunksByOrder[productIndex * selectedSampleCards.length + sampleCardIndex] = chunk;
                        }

                        registerFinishedSampleCard(productIndex, sampleCard.name, chunk ? chunk.rows.length : 0);
                    }
                    catch (error: any) {

                        if (isCancel(error)) {
                            return;
                        }

                        hasError = true;
                        updateProductProgress(productIndex, {
                            state: 'error',
                            errorMessage: `${sampleCard.name}: ${error?.message ?? ''}`
                        });
                    }
                }
            };

            const workersCount = Math.min(PARALLEL_REQUESTS, selectedSampleCards.length);
            await Promise.all(Array.from({ length: workersCount }, () => exportWorker()));

            setExportProgress((previous) => previous
                ? previous.map((item, i) => i === productIndex
                    ? { ...item, currentSampleCardName: '', state: item.state === 'error' ? item.state : 'done' as ExportItemState }
                    : item)
                : previous);
        }

        cancelSourceRef.current = null;
        setIsExporting(false);

        if (canceledRef.current) {
            setExportMessage(t('ExportReceipts.ExportCanceled'));
            return;
        }

        const chunks = chunksByOrder.filter((chunk): chunk is CsvChunk => !!chunk);
        const totalRows = chunks.reduce((sum, chunk) => sum + chunk.rows.length, 0);

        if (chunks.length === 0 || totalRows === 0) {
            setExportMessage(hasError ? t('ExportReceipts.ExportFinishedWithErrors') : t('ExportReceipts.ExportNoData'));
            return;
        }

        downloadCsv(mergeCsvChunks(chunks), t('ExportReceipts.ExportFilenameCsv'));

        setExportMessage(hasError
            ? t('ExportReceipts.ExportFinishedWithErrors')
            : t('ExportReceipts.ExportFinished', { rows: totalRows }));
    };

    const handleSpecialExportCreation = async () => {

        if (isExporting) {
            return;
        }

        canceledRef.current = false;
        setIsExporting(true);
        setExportProgress(null);
        setExportMessage(t('ExportReceipts.ExportRunningSpecial'));

        try {
            const response = await apiExportSpecialReceiptsCsv(
                formData.pigmentsUnit,
                formData.baseUnit);

            downloadCsv(response.data, t('ExportReceipts.ExportSpecialFilenameCsv'));
            setExportMessage('');
        }
        catch (error: any) {
            setExportMessage(`${t('ExportReceipts.StateError')}: ${error?.message ?? ''}`);
        }
        finally {
            setIsExporting(false);
        }
    };

    const productsOptions = allTargetProducts.map((product: any) => {
        return { 'label': product.name, 'value': product.id }
    });

    const totalSampleCardTasks = exportProgress ? exportProgress.reduce((sum, item) => sum + item.sampleCardsTotal, 0) : 0;
    const doneSampleCardTasks = exportProgress ? exportProgress.reduce((sum, item) => sum + item.sampleCardsDone, 0) : 0;
    const progressPercent = totalSampleCardTasks > 0 ? Math.round(100 * doneSampleCardTasks / totalSampleCardTasks) : 0;
    const finishedProducts = exportProgress ? exportProgress.filter((item) => item.state === 'done' || item.state === 'error').length : 0;
    const runningProductIndex = exportProgress ? exportProgress.findIndex((item) => item.state === 'running') : -1;
    const currentProductNumber = runningProductIndex >= 0 ? runningProductIndex + 1 : finishedProducts;

    const exportStateLabel = (state: ExportItemState) => {
        switch (state) {
            case 'running': return t('ExportReceipts.StateRunning');
            case 'done': return t('ExportReceipts.StateDone');
            case 'error': return t('ExportReceipts.StateError');
            default: return t('ExportReceipts.StateWaiting');
        }
    };

    return <>
        <PageContent>
            <>
                <TopBarWithBreadCrumb
                    contextLevel={props.contextLevel}
                    pageLevel="Receptury"
                />

                <div className="row" style={{ padding: '20px' }}>
                    <div className="col-6">
                        <div className={`card`}>

                            <div className="card-body">

                                <MultiSelect
                                    options={productsOptions}
                                    value={formData.targetProductIds}

                                    onChange={handleTargetProductChange}
                                    labelledBy={t('ExportReceipts.Products')}
                                    overrideStrings={{
                                        "allItemsAreSelected": t('ExportReceipts.Products'),
                                        "clearSearch": t('ExportReceipts.clearSearch'),
                                        "clearSelected": t('ExportReceipts.clearSelected'),
                                        "noOptions": t('ExportReceipts.noOptions'),
                                        "search": t('ExportReceipts.search'),
                                        "selectAll": t('ExportReceipts.selectAll'),
                                        "selectAllFiltered": t('ExportReceipts.selectAllFiltered'),
                                        "selectSomeItems": t('ExportReceipts.selectSomeItems'),
                                        "create": t('ExportReceipts.create'),
                                    }}
                                />

                                {formData.targetProductIds && formData.targetProductIds.length > 0 && <div style={{ marginBottom: '10px' }}>{formData.targetProductsAsText}</div>}
                                {!formData.targetProductIds || !formData.targetProductIds.length && <div style={{ textAlign: 'center', marginBottom: '10px' }}>Není vybrán žádný výrobek</div>}


                                <CheckBox
                                    id="allSampleCards"
                                    label="Všechny vzorkovnice"
                                    defaultValue={formData.allSampleCards}
                                    handleCheckedChange={handleSampleCardAllCheckedChange}
                                />
                                {!formData.allSampleCards &&
                                    <ComboBoxData
                                        id="sampleCards"
                                        label={t('General.Fields.SampleBook')}
                                        dataTextField="name"
                                        dataValueField="id"
                                        value={formData.sampleCardId}
                                        handleChange={handleSampleCardChange}
                                        rows={sampleCardsForProduct}
                                    />
                                }

                                <ComboBoxData
                                    id="unitSelectionBasis"
                                    label={t('General.Fields.UnitOfBase')}
                                    dataTextField="name"
                                    dataValueField="id"
                                    defaultValue={"11"}
                                    handleChange={handleUnitChangeBasis}
                                    rows={[
                                        { name: t("General.Units.Kilograms"), id: "10", code: "Kg" },
                                        { name: t("General.Units.Grams"), id: "11", code: "G" },
                                        { name: t("General.Units.Liters"), id: "21", code: "L" },
                                        { name: t("General.Units.Milliliters"), id: "22", code: "Ml" },
                                        { name: t("General.Units.Ounces"), id: "30", code: "Oz" },
                                    ]}
                                />


                                <ComboBoxData
                                    id="unitSelectionPigments"
                                    label={t('General.Fields.UnitOfPigment')}
                                    dataTextField="name"
                                    dataValueField="id"
                                    defaultValue={"11"}
                                    handleChange={handleUnitChangePigments}
                                    rows={[
                                        { name: t("General.Units.Kilograms"), id: "10", code: "Kg" },
                                        { name: t("General.Units.Grams"), id: "11", code: "G" },
                                        { name: t("General.Units.Liters"), id: "21", code: "L" },
                                        { name: t("General.Units.Milliliters"), id: "22", code: "Ml" },
                                        { name: t("General.Units.Ounces"), id: "30", code: "Oz" },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                </div>


                <div className="row" style={{ padding: '20px', justifyContent: 'center' }}>
                    <div className="col-4" style={{ textAlign: 'center' }}>
                        <button
                            style={{ width: '100%' }}
                            onClick={() => {
                                handleExportCreation();
                            }}
                            type="button"
                            disabled={isExporting}
                            className="btn-sm btn-primary"
                        >
                            {t('ExportReceipts.ButtonExport')}
                        </button>
                    </div>
                    {isReceiptAdmin &&
                        <div className="col-4" style={{ textAlign: 'center' }}>
                            <button
                                style={{ width: '100%' }}
                                onClick={() => {
                                    handleSpecialExportCreation();
                                }}
                                type="button"
                                disabled={isExporting}
                                className="btn-sm btn-primary"
                            >
                                {t('ExportReceipts.ButtonExportSpecial')}
                            </button>
                        </div>
                    }
                </div>

                {(isExporting || exportProgress || exportMessage) &&
                    <div className="row" style={{ padding: '0 20px 20px 20px' }}>
                        <div className="col-12">
                            <div className="card export-progress-card">
                                <div className="card-body">

                                    <div className="export-progress-header">
                                        <strong>{t('ExportReceipts.ProgressTitle')}</strong>
                                        {isExporting && exportProgress &&
                                            <button
                                                type="button"
                                                className="btn-sm btn-secondary"
                                                onClick={handleExportCancel}
                                            >
                                                {t('ExportReceipts.ButtonCancel')}
                                            </button>
                                        }
                                    </div>

                                    {exportProgress &&
                                        <>
                                            <div className="export-progress-summary">
                                                {t('ExportReceipts.ProgressProductXofY', { current: currentProductNumber, total: exportProgress.length })}
                                                {' · '}
                                                {t('ExportReceipts.ProgressSampleBooksXofY', { current: doneSampleCardTasks, total: totalSampleCardTasks })}
                                            </div>

                                            <div className="progress export-progress-bar">
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{ width: `${progressPercent}%` }}
                                                    aria-valuenow={progressPercent}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                >
                                                    {progressPercent}%
                                                </div>
                                            </div>

                                            <div className="export-progress-table-wrapper">
                                                <table className="table table-sm export-progress-table">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>{t('ExportReceipts.ProgressProduct')}</th>
                                                            <th>{t('ExportReceipts.ProgressSampleBook')}</th>
                                                            <th>{t('ExportReceipts.ProgressState')}</th>
                                                            <th>{t('ExportReceipts.ProgressRows')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {exportProgress.map((item, index) =>
                                                            <tr key={item.productId} className={`export-progress-row-${item.state}`}>
                                                                <td>{index + 1} / {exportProgress.length}</td>
                                                                <td>{item.productName}</td>
                                                                <td>
                                                                    {t('ExportReceipts.ProgressSampleBooksXofY', { current: item.sampleCardsDone, total: item.sampleCardsTotal })}
                                                                    {item.currentSampleCardName && <span className="export-progress-samplecard"> {item.currentSampleCardName}</span>}
                                                                </td>
                                                                <td>
                                                                    {exportStateLabel(item.state)}
                                                                    {item.errorMessage && <div className="export-progress-error">{item.errorMessage}</div>}
                                                                </td>
                                                                <td>{item.rows}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    }

                                    {exportMessage && <div className="export-progress-message">{exportMessage}</div>}

                                </div>
                            </div>
                        </div>
                    </div>
                }

            </>
        </PageContent>
    </>
}

export default ExportRecepturPage;